import { t, Lab, requireKeyOrExplain, carrierPicker, submitResult, fmtMs, fmtClock, errorText } from '../common.js';

const $ = (id) => document.getElementById(id);
const face = $('clock-face');
const timeEl = $('clock-time');
const statusEl = $('clock-status');
const fullscreenBtn = $('fullscreen');
const form = $('tiktok-form');
const delayInput = $('delay');
const noteInput = $('note');
const formMsg = $('form-msg');
const submitBtn = $('submit');

const pageStart = performance.now();
let lab = null;
let clock = null;
let online = false;
let retryTimer = null;
let retryDelay = 2000;
let submitted = 0;
let lastError = null;

Object.defineProperty(window, '__lab', {
  value: Object.freeze({
    get offsetMs() {
      return clock ? clock.offsetMs : null;
    },
    get uncertaintyMs() {
      return clock ? clock.uncertaintyMs : null;
    },
    get online() {
      return online;
    },
    get submitted() {
      return submitted;
    },
    get error() {
      return lastError;
    },
  }),
  configurable: true,
});

function setStatus(text, tone) {
  statusEl.textContent = text;
  statusEl.classList.remove('ok', 'warn', 'bad');
  if (tone) statusEl.classList.add(tone);
}

function refreshStatus() {
  if (!clock || !clock.synced) return;
  const unc = `توقيت بغداد · دقّة المزامنة ± ${fmtMs(clock.uncertaintyMs)}`;
  if (online) setStatus(unc, null);
  else setStatus(`${t.reconnecting} (${unc})`, 'warn');
}

// ---------- clock face ----------

let lastText = '';
function frame() {
  if (clock && clock.synced) {
    const s = fmtClock(clock.now());
    if (s !== lastText) {
      timeEl.textContent = s;
      lastText = s;
    }
  }
  requestAnimationFrame(frame);
}

// ---------- server connection with retry ----------

function scheduleRetry() {
  if (retryTimer) return;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    connect();
  }, retryDelay);
  retryDelay = Math.min(retryDelay * 2, 30000);
}

let connecting = false;
async function connect() {
  if (connecting) return;
  connecting = true;
  if (!clock || !clock.synced) setStatus(t.connecting, null);
  let next = null;
  try {
    next = await Lab.connect();
    next.on('close', () => {
      if (lab !== next) return;
      online = false;
      refreshStatus();
      if (!clock || !clock.synced) setStatus(t.disconnected, 'warn');
      scheduleRetry();
    });
    if (!clock || !clock.synced) setStatus(t.syncing, null);
    await next.clock.syncInitial(15);
    if (!next.isOpen) throw new Error('closed');
    next.clock.start(5000);
    lab = next;
    clock = next.clock;
    online = true;
    lastError = null;
    retryDelay = 2000;
    refreshStatus();
  } catch (err) {
    lastError = err && err.message ? err.message : String(err);
    if (next) next.close();
    if (!clock || !clock.synced) setStatus(`${errorText(err)} ${t.reconnecting}`, 'bad');
    else {
      online = false;
      refreshStatus();
    }
    scheduleRetry();
  } finally {
    connecting = false;
  }
}

// ---------- screen helpers ----------

let wakeLock = null;
async function keepAwake() {
  if (!('wakeLock' in navigator) || document.visibilityState !== 'visible' || wakeLock) return;
  try {
    wakeLock = await navigator.wakeLock.request('screen');
    wakeLock.addEventListener('release', () => {
      wakeLock = null;
    });
  } catch {
    wakeLock = null;
  }
}

function setupFullscreen() {
  if (!document.fullscreenEnabled || typeof face.requestFullscreen !== 'function') return;
  fullscreenBtn.classList.remove('hidden');
  fullscreenBtn.addEventListener('click', async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await face.requestFullscreen({ navigationUI: 'hide' });
    } catch {
      /* not allowed here */
    }
  });
  document.addEventListener('fullscreenchange', () => {
    fullscreenBtn.textContent = document.fullscreenElement ? 'خروج من ملء الشاشة' : 'ملء الشاشة';
  });
}

// ---------- TikTok manual form ----------

const DIGITS = { '٠': 0, '١': 1, '٢': 2, '٣': 3, '٤': 4, '٥': 5, '٦': 6, '٧': 7, '٨': 8, '٩': 9 };
function parseDelay(raw) {
  let s = String(raw || '').trim();
  s = s.replace(/[٠-٩]/g, (d) => String(DIGITS[d]));
  s = s.replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0));
  s = s.replace(/[٫,]/g, '.').replace(/[\s٬]/g, '');
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const v = Math.round(Number(s));
  return Number.isFinite(v) && v >= 0 && v <= 120000 ? v : null;
}

function showFormMsg(text, tone) {
  formMsg.textContent = text;
  formMsg.classList.remove('hidden', 'ok', 'warn', 'bad');
  if (tone) formMsg.classList.add(tone);
}

function setupForm() {
  const picker = carrierPicker($('carrier'), () => {
    if (!formMsg.classList.contains('ok')) formMsg.classList.add('hidden');
  });

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const carrier = picker.value;
    if (!carrier) {
      showFormMsg(t.carrierRequired, 'bad');
      return;
    }
    const delayMs = parseDelay(delayInput.value);
    if (delayMs === null) {
      showFormMsg('اكتب التأخير رقماً بالملّي ثانية، بين 0 و120000.', 'bad');
      delayInput.focus();
      return;
    }
    const payload = {
      carrier,
      note: noteInput.value.trim().slice(0, 60),
      durationS: Math.round((performance.now() - pageStart) / 1000),
      delayMs,
    };
    if (clock && Number.isFinite(clock.uncertaintyMs)) {
      payload.clockUncertaintyMs = Math.round(clock.uncertaintyMs * 10) / 10;
    }

    submitBtn.disabled = true;
    showFormMsg(t.submitting, null);
    try {
      await submitResult('tiktok_manual', payload);
      submitted++;
      showFormMsg(`${t.submitted} (${fmtMs(delayMs)})`, 'ok');
      delayInput.value = '';
    } catch (err) {
      showFormMsg(`${t.submitFailed} ${errorText(err)}`, 'bad');
    } finally {
      submitBtn.disabled = false;
    }
  });
}

// ---------- start ----------

setupFullscreen();

if (requireKeyOrExplain($('key-notice'))) {
  setupForm();
  requestAnimationFrame(frame);
  setInterval(refreshStatus, 1000);
  keepAwake();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      keepAwake();
      if (!online && !retryTimer) connect();
    }
  });
  connect();
} else {
  setStatus(t.missingKey, 'bad');
  form.classList.add('hidden');
}
